import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { RolesAdminService } from '@/core/api/sicom-api/sistema/roles/roles-admin.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { montarRole, roleValida } from '../roles-agrupamento.util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export interface RoleEmEdicao {
    name: string;
    description?: string;
}

@Component({
    selector: 'app-role-detalhe',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, Dialog, ButtonModule, InputTextModule, AutoCompleteModule],
    templateUrl: './role-detalhe.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Diálogo de criação (módulo.funcionalidade.ação) ou edição de descrição de uma role. */
export class RoleDetalhe {
    private readonly service = inject(RolesAdminService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly destroyRef = inject(DestroyRef);

    showDialog = model<boolean>(false);
    modulosSugeridos = input<string[]>([]);
    funcionalidadesSugeridas = input<string[]>([]);
    /** Quando informado, o dialog edita só a descrição da role existente. */
    edicao = input<RoleEmEdicao | null>(null);
    salvou = output<void>();

    protected readonly modulo = signal('');
    protected readonly funcionalidade = signal('');
    protected readonly acao = signal('');
    protected readonly descricao = signal('');
    protected readonly salvando = signal(false);
    protected readonly sugestoes = signal<string[]>([]);

    protected readonly modoEdicao = computed(() => !!this.edicao());
    protected readonly preview = computed(() => (this.modoEdicao() ? this.edicao()!.name : montarRole(this.modulo(), this.funcionalidade(), this.acao())));
    protected readonly valido = computed(() => this.modoEdicao() || (!!this.modulo().trim() && !!this.funcionalidade().trim() && roleValida(this.preview())));

    constructor() {
        // Ao abrir em modo edição, pré-carrega a descrição da role.
        effect(() => {
            const e = this.edicao();
            if (this.showDialog() && e) this.descricao.set(e.description ?? '');
        });
    }

    protected filtrar(base: string[], ev: { query: string }): void {
        const q = ev.query.toLowerCase();
        this.sugestoes.set(base.filter((s) => s.toLowerCase().includes(q)));
    }

    /** O '.' é o separador da role — bloqueado nos inputs (cada campo é um segmento). */
    protected bloquearPonto(e: KeyboardEvent): void {
        if (e.key === '.') e.preventDefault();
    }
    private semPonto(v: string): string {
        return (v ?? '').replace(/\./g, '');
    }
    protected setModulo(v: string): void {
        this.modulo.set(this.semPonto(v));
    }
    protected setFuncionalidade(v: string): void {
        this.funcionalidade.set(this.semPonto(v));
    }
    protected setAcao(v: string): void {
        this.acao.set(this.semPonto(v));
    }

    protected salvar(): void {
        if (!this.valido()) return;
        this.salvando.set(true);
        const op$ = this.modoEdicao() ? this.service.atualizarRole(this.edicao()!.name, this.descricao().trim() || undefined) : this.service.criarRole({ name: this.preview(), description: this.descricao().trim() || undefined });
        op$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
                this.notificacoes.notificar({ severity: 'success', summary: this.modoEdicao() ? 'Descrição atualizada.' : 'Role criada.' });
                this.salvando.set(false);
                this.resetar();
                this.salvou.emit();
            },
            error: (e: HttpErrorResponse) => {
                const msg = this.modoEdicao() ? 'Erro ao atualizar descrição.' : e.status === 409 ? 'Role já existe.' : 'Erro ao criar role.';
                this.notificacoes.notificar({ toast: false, severity: 'error', summary: msg });
                this.salvando.set(false);
            }
        });
    }
    protected fechar(): void {
        this.showDialog.set(false);
        this.resetar();
    }
    private resetar(): void {
        this.modulo.set('');
        this.funcionalidade.set('');
        this.acao.set('');
        this.descricao.set('');
    }
}
